import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest, merge, of } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, finalize, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { OdooFichaService } from 'src/app/services/odoo/odoo-ficha.service';
import { validarCorreoOpcional, validarTelefonoOpcional } from 'src/app/services/odoo/odoo-ficha-reglas';
import {
  OdooFicha, OdooFichaCambios, OdooFichaDato, OdooFichaMovimiento, OdooFichaTipo, OdooResultado, OdooResultadoTipo,
} from 'src/app/services/odoo/odoo-ficha.models';
import { OdooCarga } from 'src/app/services/odoo/odoo.models';
import { conCarga, mensajeDeFallo } from 'src/app/services/odoo/odoo.service';

export type FichaPestana = 'revisar' | 'anotar' | 'actualizar' | 'historial';
type Accion = 'revisar' | 'anotar' | 'actualizar';

interface PestanaDef { id: FichaPestana; texto: string; icono: string; }
interface FichaVm { f: OdooFicha; filas: { a: OdooFichaDato; b: OdooFichaDato | null }[]; }

const PESTANAS: PestanaDef[] = [
  { id: 'revisar', texto: 'Revisar', icono: 'eye' },
  { id: 'anotar', texto: 'Anotar', icono: 'upload' },
  { id: 'actualizar', texto: 'Actualizar', icono: 'refresh' },
  { id: 'historial', texto: 'Historial', icono: 'history' },
];

const TITULOS: Record<OdooResultadoTipo, string> = {
  exito: 'Listo',
  ya_existia: 'Ya estaba en Odoo',
  datos: 'Odoo no pudo recibir los datos',
  transitorio: 'No se pudo hablar con Odoo',
  ocupado: 'Hay un envío en curso',
  apagado: 'La integración con Odoo está apagada',
  error: 'No se pudo completar',
};

/** Los textos que cambian según sea el cliente o el comercio: la plantilla solo lee `t.<clave>`. */
export interface FichaTextos {
  permisoEscribir: string;
  /** Va dentro de la ficha del comercio (encabezado propio): sin «Volver al perfil» y con h2 en vez de h1. */
  anidada: boolean;
  /** Muestra «Otro teléfono» y la nota de que Odoo guarda un solo teléfono. */
  dosTelefonos: boolean;
  sujeto: string;
  revisarAl: string;
  datosDel: string;
  esteSujeto: string;
  faltaPara: string;
  lista: string;
  darDeAlta: string;
  noEstaEnOdoo: string;
  primeroAnotalo: string;
  introActualizar: string;
  etiquetaNombre: string;
  etiquetaId: string;
  notaActualizar: string;
  sinPermisoAnotar: string;
  sinPermisoActualizar: string;
  historialLinea: string;
  historialVacio: string;
  historialCaption: string;
}

const TEXTOS: Record<OdooFichaTipo, FichaTextos> = {
  cliente: {
    permisoEscribir: 'contactos.gestion_odoo.write',
    anidada: false,
    dosTelefonos: true,
    sujeto: 'Cliente',
    revisarAl: 'al cliente',
    datosDel: 'Datos del cliente',
    esteSujeto: 'este cliente',
    faltaPara: 'enviarla',
    lista: 'lista',
    darDeAlta: 'Dar de alta al cliente en Odoo',
    noEstaEnOdoo: 'Este cliente todavía no está en Odoo',
    primeroAnotalo: 'Primero anótalo; después podrás corregir su correo o teléfonos aquí.',
    introActualizar: 'Corrige el correo o los teléfonos y envíalos a Odoo. El nombre y la cédula no se cambian aquí.',
    etiquetaNombre: 'Nombre',
    etiquetaId: 'Cédula',
    notaActualizar: 'Odoo guarda un solo teléfono: se envía el principal y, si lo dejas vacío, el otro. Lo que dejes vacío no se cambia en Odoo. Los datos del cliente en CrediTotal no se modifican desde aquí.',
    sinPermisoAnotar: 'No tienes permiso para anotar clientes en Odoo.',
    sinPermisoActualizar: 'No tienes permiso para actualizar clientes en Odoo.',
    historialLinea: 'Cada vez que se revisa, anota o actualiza a este cliente queda una línea.',
    historialVacio: 'Todavía no hay movimientos de este cliente',
    historialCaption: 'Movimientos de este cliente en Odoo, del más reciente al más antiguo',
  },
  comercio: {
    permisoEscribir: 'config.comercios_odoo.write',
    anidada: true,
    dosTelefonos: false,
    sujeto: 'Comercio',
    revisarAl: 'al comercio',
    datosDel: 'Datos del comercio',
    esteSujeto: 'este comercio',
    faltaPara: 'enviarlo',
    lista: 'listo',
    darDeAlta: 'Dar de alta al comercio en Odoo',
    noEstaEnOdoo: 'Este comercio todavía no está en Odoo',
    primeroAnotalo: 'Primero anótalo; después podrás corregir su correo o teléfono aquí.',
    introActualizar: 'Corrige el correo o el teléfono y envíalos a Odoo. La razón social y el RIF no se cambian aquí.',
    etiquetaNombre: 'Razón social',
    etiquetaId: 'RIF',
    notaActualizar: 'Lo que dejes vacío no se cambia en Odoo. Los datos del comercio en CrediTotal no se modifican desde aquí.',
    sinPermisoAnotar: 'No tienes permiso para anotar comercios en Odoo.',
    sinPermisoActualizar: 'No tienes permiso para actualizar comercios en Odoo.',
    historialLinea: 'Cada vez que se revisa, anota o actualiza a este comercio queda una línea.',
    historialVacio: 'Todavía no hay movimientos de este comercio',
    historialCaption: 'Movimientos de este comercio en Odoo, del más reciente al más antiguo',
  },
};

/** Pura: la tabla de datos de a dos (dato + dato) como en el plan. */
export function emparejar(datos: OdooFichaDato[]): { a: OdooFichaDato; b: OdooFichaDato | null }[] {
  const filas: { a: OdooFichaDato; b: OdooFichaDato | null }[] = [];
  for (let i = 0; i < datos.length; i += 2) filas.push({ a: datos[i], b: datos[i + 1] || null });
  return filas;
}

/**
 * GESTIÓN EN ODOO dentro de la ficha del cliente — `console/person/odoo/:code` — o del comercio —
 * `console/merchants/:code/odoo` (ruta hija; `data.tipo = 'comercio'`). Una sola pantalla, los textos salen de TEXTOS.
 * Pestañas: Revisar (valida antes de enviar), Anotar (alta a mano), Actualizar (correo/teléfonos) e Historial.
 * Todo lo decide el servidor (reglas del contrato de 3mit, «ya existe» = éxito, datos vs. transitorio); aquí solo se muestra.
 * La capa visual `od-*` se carga aquí (sin encapsulación) porque la pantalla vive fuera del panel de Odoo.
 */
@Component({
  selector: 'app-contact-odoo',
  templateUrl: './contact-odoo.component.html',
  styleUrls: ['../odoo/odoo-ui.css', './contact-odoo.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ContactOdooComponent implements OnDestroy {
  readonly pestanas = PESTANAS;
  readonly esperaSkeleton = [0, 1, 2, 3];
  readonly tipo: OdooFichaTipo = this.route.snapshot.data['tipo'] === 'comercio' ? 'comercio' : 'cliente';
  readonly t: FichaTextos = TEXTOS[this.tipo];
  readonly puedeEscribir = this.auth.hasPermission(this.t.permisoEscribir);

  code = '';
  private base: OdooFichaCambios = { email: '', telefono: '', otro_telefono: '' };
  private ultima: Accion | null = null;
  private opener: HTMLElement | null = null;
  verTecnico = new Set<string>();

  readonly form: FormGroup = this.fb.group({
    email: ['', [validarCorreoOpcional]],
    telefono: ['', [validarTelefonoOpcional]],
    otro_telefono: ['', [validarTelefonoOpcional]],
  });

  private readonly destroy$ = new Subject<void>();
  private readonly recarga$ = new BehaviorSubject<number>(0);
  private readonly movTick$ = new BehaviorSubject<number>(0);
  private readonly fichaNueva$ = new Subject<OdooFicha>();
  private readonly pedir$ = new Subject<Accion>();

  readonly pestana$ = new BehaviorSubject<FichaPestana>(this.pestanaInicial());
  readonly ocupado$ = new BehaviorSubject<Accion | null>(null);
  readonly resultado$ = new BehaviorSubject<OdooResultado | null>(null);
  readonly confirmar$ = new BehaviorSubject<'anotar' | 'actualizar' | null>(null);

  /** El :code lo trae la propia ruta (cliente) o el padre (el comercio es ruta hija de la ficha). */
  private readonly code$ = combineLatest([this.route.paramMap, this.route.parent ? this.route.parent.paramMap : of(null)]).pipe(
    map(([p, padre]) => p.get('code') || padre?.get('code') || ''), distinctUntilChanged(), takeUntil(this.destroy$), shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Ficha: carga inicial/recarga + lo que devuelven las acciones (sin volver a pedirla). */
  readonly ficha$: Observable<OdooCarga<FichaVm>> = merge(
    conCarga(combineLatest([this.code$, this.recarga$]), ([code]) => (code ? this.api.ficha(code, this.tipo) : EMPTY)).pipe(
      tap((v) => { if (v.datos && !this.form.dirty) this.llenarForm(v.datos.editable); }),
    ),
    this.fichaNueva$.pipe(map((f): OdooCarga<OdooFicha> => ({ cargando: false, error: null, datos: f }))),
  ).pipe(
    map((v): OdooCarga<FichaVm> => ({ ...v, datos: v.datos ? { f: v.datos, filas: emparejar(v.datos.datos) } : null })),
    takeUntil(this.destroy$),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Historial: se pide al abrir la pestaña (el async pipe vive solo dentro de ella). */
  readonly movs$: Observable<OdooCarga<OdooFichaMovimiento[]>> = conCarga(
    combineLatest([this.code$, this.movTick$]), ([code]) => this.api.movimientos(code, 30, this.tipo));

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private api: OdooFichaService,
    private auth: AuthService,
    private cdr: ChangeDetectorRef,
  ) {
    this.code$.subscribe((c) => { this.code = c; this.resultado$.next(null); });
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.cdr.markForCheck());
    this.pedir$.pipe(exhaustMap((a) => this.ejecutar(a)), takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pestanaInicial(): FichaPestana {
    const p = this.route.snapshot.queryParamMap.get('pestana');
    return PESTANAS.some((x) => x.id === p) ? (p as FichaPestana) : 'revisar';
  }

  private llenarForm(e: OdooFichaCambios): void {
    this.base = { email: e.email || '', telefono: e.telefono || '', otro_telefono: this.t.dosTelefonos ? (e.otro_telefono || '') : '' };
    this.form.reset(this.base);
  }

  // ── Pestañas ───────────────────────────────────────────────────────────────────────────────────
  irA(p: FichaPestana): void { this.pestana$.next(p); }

  teclas(ev: KeyboardEvent, activa: FichaPestana): void {
    const i = PESTANAS.findIndex((p) => p.id === activa);
    let n = -1;
    if (ev.key === 'ArrowRight') n = (i + 1) % PESTANAS.length;
    else if (ev.key === 'ArrowLeft') n = (i - 1 + PESTANAS.length) % PESTANAS.length;
    else if (ev.key === 'Home') n = 0;
    else if (ev.key === 'End') n = PESTANAS.length - 1;
    if (n < 0) return;
    ev.preventDefault();
    this.irA(PESTANAS[n].id);
    setTimeout(() => (document.getElementById('od-ficha-tab-' + PESTANAS[n].id) as HTMLElement | null)?.focus());
  }

  volver(): void { this.router.navigate(['/console/person/profile', this.code]); }
  recargar(): void { this.recarga$.next(this.recarga$.value + 1); }
  recargarHistorial(): void { this.movTick$.next(this.movTick$.value + 1); }

  // ── Acciones ───────────────────────────────────────────────────────────────────────────────────
  revisar(): void { this.pedir$.next('revisar'); }

  /** Abre la confirmación (anotar y actualizar siempre la piden). */
  pedirConfirmacion(a: 'anotar' | 'actualizar'): void {
    if (this.ocupado$.value) return;
    if (a === 'actualizar' && (this.form.invalid || !this.hayCambios)) { this.form.markAllAsTouched(); return; }
    this.opener = document.activeElement as HTMLElement | null;
    this.confirmar$.next(a);
    setTimeout(() => document.getElementById('od-ficha-conf-cancelar')?.focus());
  }

  cerrarConfirmacion(): void {
    if (!this.confirmar$.value) return;
    this.confirmar$.next(null);
    const o = this.opener; this.opener = null;
    setTimeout(() => o?.focus());
  }

  confirmarAccion(): void {
    const a = this.confirmar$.value;
    if (!a) return;
    this.cerrarConfirmacion();
    this.pedir$.next(a);
  }

  reintentar(): void { if (this.ultima && this.ultima !== 'revisar') this.pedir$.next(this.ultima); }
  cerrarResultado(): void { this.resultado$.next(null); }

  get hayCambios(): boolean {
    const v = this.form.getRawValue() as OdooFichaCambios;
    return (['email', 'telefono', 'otro_telefono'] as const).some((k) => (v[k] || '').trim() !== this.base[k]);
  }

  descartar(): void { this.form.reset(this.base); }

  /** Lo que se confirma en el diálogo de «Actualizar». */
  cambiosParaMostrar(): { etiqueta: string; valor: string }[] {
    const v = this.form.getRawValue() as OdooFichaCambios;
    const filas = [{ etiqueta: 'Correo', valor: v.email }, { etiqueta: 'Teléfono', valor: v.telefono }];
    if (this.t.dosTelefonos) filas.push({ etiqueta: 'Otro teléfono', valor: v.otro_telefono });
    return filas.filter((x) => (x.valor || '').trim()).map((x) => ({ etiqueta: x.etiqueta, valor: x.valor.trim() }));
  }

  private ejecutar(a: Accion): Observable<unknown> {
    const code = this.code;
    if (!code) return EMPTY;
    this.ocupado$.next(a);
    this.resultado$.next(null);
    if (a !== 'revisar') this.ultima = a;
    const cuerpo = this.form.getRawValue() as OdooFichaCambios;
    const llamada$: Observable<unknown> = a === 'revisar'
      ? this.api.revisar(code, this.tipo).pipe(tap((f) => this.fichaNueva$.next(f)))
      : (a === 'anotar' ? this.api.anotar(code, this.tipo) : this.api.actualizar(code, {
          email: (cuerpo.email || '').trim(), telefono: (cuerpo.telefono || '').trim(), otro_telefono: (cuerpo.otro_telefono || '').trim(),
        }, this.tipo)).pipe(tap((r) => {
          this.resultado$.next(r.resultado);
          this.fichaNueva$.next(r.ficha);
          if (a === 'actualizar' && r.resultado.exito) {
            this.base = { email: (cuerpo.email || '').trim(), telefono: (cuerpo.telefono || '').trim(), otro_telefono: (cuerpo.otro_telefono || '').trim() };
            this.form.reset(this.base);
          }
          this.movTick$.next(this.movTick$.value + 1);
        }));
    return llamada$.pipe(
      catchError((e: unknown) => {
        const status = e instanceof HttpErrorResponse ? e.status : 0;
        this.resultado$.next({ exito: false, tipo: 'error', reintentable: status === 0 || status >= 500, mensaje: mensajeDeFallo(e) });
        if (status === 409 || status === 404) this.recargar(); // el estado cambió: se vuelve a leer
        return of(null);
      }),
      finalize(() => { this.ocupado$.next(null); this.cdr.markForCheck(); }),
    );
  }

  // ── Historial ──────────────────────────────────────────────────────────────────────────────────
  alternarTecnico(id: string): void {
    const n = new Set(this.verTecnico);
    if (n.has(id)) n.delete(id); else n.add(id);
    this.verTecnico = n;
  }

  titulo(r: OdooResultado): string { return TITULOS[r.tipo] || TITULOS.error; }

  @HostListener('document:keydown.escape') alEscape(): void { this.cerrarConfirmacion(); }

  /** Foco atrapado en el diálogo. */
  atrapar(ev: KeyboardEvent, dialogo: HTMLElement): void {
    if (ev.key !== 'Tab') return;
    const f = Array.from(dialogo.querySelectorAll<HTMLElement>('button:not([disabled]), input, [href]'));
    if (!f.length) return;
    const primero = f[0], ultimo = f[f.length - 1], activo = document.activeElement;
    if (ev.shiftKey && activo === primero) { ev.preventDefault(); ultimo.focus(); }
    else if (!ev.shiftKey && activo === ultimo) { ev.preventDefault(); primero.focus(); }
    else if (!dialogo.contains(activo)) { ev.preventDefault(); primero.focus(); }
  }

  campoInvalido(c: string): boolean { const x = this.form.get(c); return !!x && x.invalid && (x.touched || x.dirty); }

  porId = (_: number, p: PestanaDef): string => p.id;
  porFila = (i: number): number => i;
  porMov = (_: number, m: OdooFichaMovimiento): string => m.id;
  porTexto = (i: number, t: string): string => `${i}-${t}`;
}
